import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Clock, User } from "lucide-react";
import BookingModal from "./BookingModal";
import { Eyebrow, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parseDate = (iso) => new Date(`${iso}T12:00:00`);

const fmtWeekday = (iso) =>
    parseDate(iso).toLocaleDateString("it-IT", { weekday: "short" });

const fmtShort = (iso) =>
    parseDate(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short" });

const fmtLong = (iso) =>
    parseDate(iso).toLocaleDateString("it-IT", {
        weekday: "long",
        day: "numeric",
        month: "long",
    });

export default function ClassSchedule() {
    const [days, setDays] = useState(null);
    const [error, setError] = useState(false);
    const [dayIdx, setDayIdx] = useState(0);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setError(false);
        try {
            const res = await axios.get(`${API}/schedule`);
            setDays(res.data.days);
        } catch {
            setError(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const activeDay = days?.[dayIdx];

    return (
        <section id="classi" data-testid="class-schedule" className="bg-white px-5 py-24 sm:px-8 md:py-32">
            <div className="mx-auto max-w-6xl">
                <Reveal>
                    <Eyebrow>Le classi · prossimi 15 giorni</Eyebrow>
                    <div className="mt-6 flex flex-wrap items-end justify-between gap-6 border-b border-hairline pb-10">
                        <h2 className="max-w-md text-3xl font-light leading-tight tracking-[-0.02em] text-ink sm:text-5xl">
                            Prenota il tuo posto
                        </h2>
                        <p className="max-w-xs text-sm font-light leading-relaxed text-body">
                            Conferma immediata, senza registrazione. Caparra online
                            o pagamento in studio.
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.1} className="mt-10">
                    {error && (
                        <div className="border border-hairline bg-white p-8 text-center">
                            <p className="text-body">Non riesco a caricare gli orari.</p>
                            <button
                                data-testid="schedule-retry-button"
                                onClick={load}
                                className="mt-4 bg-ink px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-white"
                            >
                                Riprova
                            </button>
                        </div>
                    )}

                    {!days && !error && (
                        <div className="space-y-4">
                            <div className="h-12 animate-pulse bg-linenSoft" />
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="h-24 animate-pulse bg-linenSoft" />
                            ))}
                        </div>
                    )}

                    {days && (
                        <>
                            <div className="-mx-5 flex gap-0 overflow-x-auto px-5 sm:px-0">
                                {days.map((d, i) => (
                                    <button
                                        key={d.key}
                                        data-testid={`schedule-day-${d.date}`}
                                        onClick={() => setDayIdx(i)}
                                        className={`flex shrink-0 flex-col items-center border border-hairline px-4 py-2.5 transition-all duration-300 active:scale-[0.97] ${
                                            i === dayIdx
                                                ? "border-ink bg-ink text-white"
                                                : "bg-white text-body hover:border-ink hover:text-ink"
                                        } ${i > 0 ? "-ml-px" : ""}`}
                                    >
                                        <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">
                                            {fmtWeekday(d.date)}
                                        </span>
                                        <span
                                            className={`mt-0.5 text-[10px] font-medium tracking-[0.08em] ${
                                                i === dayIdx ? "text-white/70" : "text-mute"
                                            }`}
                                        >
                                            {fmtShort(d.date)}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.2em] text-mute">
                                {fmtLong(activeDay.date)}
                            </p>

                            <div className="mt-4">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeDay.key}
                                        initial={{ opacity: 0, y: 14 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                        className="space-y-px"
                                    >
                                        {activeDay.slots.map((slot) => {
                                            const full = slot.available === 0;
                                            return (
                                                <div
                                                    key={slot.id}
                                                    data-testid={`class-slot-card-${activeDay.date}-${slot.id}`}
                                                    className="group flex flex-wrap items-center gap-4 border border-hairline bg-white p-5 transition-colors duration-300 hover:border-ink sm:p-6"
                                                >
                                                    <div className="w-20 shrink-0">
                                                        <p className="text-3xl font-light tracking-tight text-ink">
                                                            {slot.time}
                                                        </p>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h3 className="text-base font-semibold tracking-tight text-ink">
                                                            {slot.class_name}
                                                        </h3>
                                                        <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-medium uppercase tracking-[0.14em] text-mute">
                                                            <span className="flex items-center gap-1.5">
                                                                <User className="h-3.5 w-3.5" />
                                                                {slot.instructor}
                                                            </span>
                                                            <span className="flex items-center gap-1.5">
                                                                <Clock className="h-3.5 w-3.5" />
                                                                {slot.duration_min} min
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <span
                                                        className={`border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] ${
                                                            full
                                                                ? "border-hairline bg-linenSoft text-mute"
                                                                : slot.available <= 2
                                                                  ? "border-ochre/40 bg-ochre/10 text-ochre"
                                                                  : "border-hairline bg-linen text-ink"
                                                        }`}
                                                    >
                                                        {full
                                                            ? "Completo"
                                                            : slot.available <= 2
                                                              ? `Ultimi ${slot.available} posti`
                                                              : `${slot.available} posti liberi`}
                                                    </span>
                                                    <button
                                                        data-testid={`class-slot-book-button-${activeDay.date}-${slot.id}`}
                                                        disabled={full}
                                                        onClick={() =>
                                                            setSelected({
                                                                slot,
                                                                dateISO: activeDay.date,
                                                                dateLabel: fmtLong(activeDay.date),
                                                            })
                                                        }
                                                        className={`px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] transition-all duration-200 active:scale-[0.97] ${
                                                            full
                                                                ? "cursor-not-allowed bg-linenSoft text-mute"
                                                                : "bg-ink text-white hover:bg-charcoal"
                                                        }`}
                                                    >
                                                        {full ? "Esaurito" : "Prenota"}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </>
                    )}
                </Reveal>
            </div>

            <AnimatePresence>
                {selected && (
                    <BookingModal
                        slot={selected.slot}
                        dateISO={selected.dateISO}
                        dateLabel={selected.dateLabel}
                        onClose={() => setSelected(null)}
                        onBooked={load}
                    />
                )}
            </AnimatePresence>
        </section>
    );
}
